import React, { useState, useEffect } from 'react';
import { gestionarMovimiento, obtenerUrlArchivo } from '../servicios/api';

const ModalCrearIngreso = ({ isOpen, onClose, onIngresoCreado, ingresoAEditar }) => {
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [cliente, setCliente] = useState('');
  const [concepto, setConcepto] = useState('');
  const [monto, setMonto] = useState('');
  const [responsable, setResponsable] = useState('');
  const [justificante, setJustificante] = useState(null);
  const [justificanteExistente, setJustificanteExistente] = useState(null); // Nuevo estado para justificante existente
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) { // Solo inicializar/resetear cuando el modal se abre
      if (ingresoAEditar) {
        setFecha(ingresoAEditar.fecha);
        setCliente(ingresoAEditar.cliente || '');
        setConcepto(ingresoAEditar.concepto || '');
        setMonto(ingresoAEditar.monto.toString());
        setResponsable(ingresoAEditar.tecnico || '');
        setJustificante(null); // Resetear el input de archivo
        setJustificanteExistente(ingresoAEditar.justificante || null);
      } else {
        // Resetear formulario para nueva creación
        setFecha(new Date().toISOString().slice(0, 10));
        setCliente('');
        setConcepto('');
        setMonto('');
        setResponsable('');
        setJustificante(null);
        setJustificanteExistente(null);
      }
      setError(null); // Limpiar errores al abrir
    }
  }, [isOpen, ingresoAEditar]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData();
    formData.append('tipo_movimiento', 'ingreso'); // Cambiado de tipo_registro a tipo_movimiento
    formData.append('fecha', fecha);
    formData.append('cliente', cliente);
    formData.append('concepto', concepto);
    formData.append('monto', monto);
    formData.append('responsable', responsable);

    if (ingresoAEditar) {
      formData.append('id_movimiento', ingresoAEditar.id_movimiento); // Añadir ID para actualización
      if (justificante) {
        formData.append('justificante', justificante);
      } else if (justificanteExistente) {
        formData.append('justificante_existente', justificanteExistente); // Mantener justificante existente
      }
    } else {
      if (justificante) {
        formData.append('justificante', justificante);
      }
    }

    try {
      const response = await gestionarMovimiento(formData); // Usar gestionarMovimiento
      if (response.exito) {
        alert(response.mensaje);
        if (onIngresoCreado) {
          onIngresoCreado(); // Recargar datos en el componente padre
        }
        onClose();
      } else {
        setError(response.mensaje || 'Error al gestionar el ingreso.');
      }
    } catch (err) {
        const errorMsg = err.response?.data?.mensaje || 'Error de conexión con el servidor.';
        setError(errorMsg);
        console.error('Error al gestionar ingreso:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
      <div className="bg-gray-900 p-6 rounded-lg shadow-lg w-full max-w-sm text-white max-h-screen overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 text-blue-500">{ingresoAEditar ? 'Editar Ingreso' : 'Registrar Otro Ingreso'}</h2>
        {error && <div className="bg-red-500/20 p-3 rounded-lg text-red-300 mb-4">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="fechaIngreso" className="block text-sm font-bold mb-1">Fecha</label>
            <input 
              type="date" 
              id="fechaIngreso" 
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-blue-500"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="clienteIngreso" className="block text-sm font-bold mb-1">Cliente</label>
            <input 
              type="text" 
              id="clienteIngreso" 
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-blue-500"
              value={cliente}
              onChange={(e) => setCliente(e.target.value)}
              placeholder="Nombre del cliente"
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="conceptoIngreso" className="block text-sm font-bold mb-1">Concepto</label>
            <input 
              type="text" 
              id="conceptoIngreso" 
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-blue-500"
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              placeholder="Ej: Venta de material, servicio de instalación..."
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="montoIngreso" className="block text-sm font-bold mb-1">Monto</label>
            <input 
              type="number" 
              id="montoIngreso" 
              step="0.01" 
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-blue-500"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder="Ej: 100.00"
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="responsableIngreso" className="block text-sm font-bold mb-1">Responsable</label>
            <input 
              type="text" 
              id="responsableIngreso" 
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-blue-500"
              value={responsable}
              onChange={(e) => setResponsable(e.target.value)}
              placeholder="Ej: Juan Pérez"
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="justificanteIngreso" className="block text-sm font-bold mb-1">Justificante (Opcional)</label>
            <input 
              type="file" 
              id="justificanteIngreso" 
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-blue-500 file:mr-4 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              onChange={(e) => setJustificante(e.target.files[0])}
            />
            {justificanteExistente && !justificante && (
              <p className="text-xs text-gray-400 mt-1">Archivo actual: <a href={obtenerUrlArchivo(`uploads/movimientos/${justificanteExistente}`)} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{justificanteExistente}</a></p>
            )}
          </div>
          
          <div className="flex items-center justify-between mt-4">
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1.5 px-4 text-sm rounded focus:outline-none focus:shadow-outline"
              disabled={loading}
            >
              {loading ? 'Guardando...' : (ingresoAEditar ? 'Actualizar Ingreso' : 'Guardar Ingreso')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-1.5 px-4 text-sm rounded focus:outline-none focus:shadow-outline"
              disabled={loading}
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalCrearIngreso;